"use client";

import { useEffect, useMemo, useRef } from "react";
import type { SupportedLanguage } from "@/generated/fastapi";
import { SUPPORTED_LANGUAGE_CODES } from "@/shared/config/backend";
import { useOptionItems } from "@/shared/ui/controls/useOptionItems";
import {
  detectLanguageCode,
  languageCodeToNameMap,
  resolveSupportedLanguage,
} from "./detection";

type LanguageChangeReason = "manual" | "auto" | "reset";

export default function useAutoDetectedLanguage({
  text,
  selectedLanguage,
  onLanguageChange,
}: {
  text: string;
  selectedLanguage: string;
  onLanguageChange: (
    nextLanguage: SupportedLanguage | "",
    reason: LanguageChangeReason,
  ) => void;
}) {
  const { languageItems } = useOptionItems();
  const lastAutoDetectedLanguageRef = useRef<string>("");

  const normalizedText = useMemo(() => text.trim(), [text]);

  const detectedLanguage = useMemo(() => {
    if (!normalizedText) return null;
    const detected = detectLanguageCode(normalizedText);
    return detected === "unknown" ? null : detected;
  }, [normalizedText]);

  const autoDetectedLanguage = useMemo(() => {
    if (!detectedLanguage) return "";
    return resolveSupportedLanguage(detectedLanguage);
  }, [detectedLanguage]);

  useEffect(() => {
    if (!normalizedText) {
      lastAutoDetectedLanguageRef.current = "";
      onLanguageChange("", "reset");
      return;
    }

    if (!autoDetectedLanguage) return;

    const canAutofillLanguage =
      selectedLanguage === "" ||
      selectedLanguage === lastAutoDetectedLanguageRef.current;

    if (!canAutofillLanguage) {
      return;
    }

    lastAutoDetectedLanguageRef.current = autoDetectedLanguage;
    onLanguageChange(autoDetectedLanguage, "auto");
  }, [autoDetectedLanguage, normalizedText, onLanguageChange, selectedLanguage]);

  const selectedLanguages = useMemo<SupportedLanguage[]>(
    () =>
      selectedLanguage &&
      SUPPORTED_LANGUAGE_CODES.includes(selectedLanguage as SupportedLanguage)
        ? [selectedLanguage as SupportedLanguage]
        : [],
    [selectedLanguage],
  );

  const handleLanguageSelection = (languages: SupportedLanguage[]) => {
    const nextLanguage = languages[0] ?? "";
    onLanguageChange(nextLanguage, "manual");
  };

  const isDetectedLanguageSupported = Boolean(
    detectedLanguage &&
      SUPPORTED_LANGUAGE_CODES.includes(
        detectedLanguage as (typeof SUPPORTED_LANGUAGE_CODES)[number],
      ),
  );

  return {
    languageItems,
    languageCodeToNameMap,
    detectedLanguage,
    autoDetectedLanguage,
    isDetectedLanguageSupported,
    selectedLanguages,
    handleLanguageSelection,
  };
}
